package ai.wagemix.regression

import ai.wagemix.net.models.WMStatsCollection
import kotlin.time.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * Result of processing a stats collection with backfilling and projection
 */
data class ProcessedStatsCollectionResult(
    val statsCollection: WMStatsCollection,
    val hasBackfilledData: Boolean
)

/**
 * Processes a WMStatsCollection by:
 * 1. Backfilling missing years in the historical data range (e.g., missing 2021 in 2020-2024 range)
 * 2. Projecting forward to the target year if needed
 *
 * This ensures consistent data processing across the application.
 *
 * @param targetYear The year to project to (defaults to current year)
 * @param minHistoricalYear Optional override for the minimum year (defaults to earliest year in data)
 * @return ProcessedStatsCollectionResult with processed data and backfill flag
 */
fun WMStatsCollection.processWithBackfillAndProjection(
    targetYear: Int = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year,
    minHistoricalYear: Int? = null
): ProcessedStatsCollectionResult {
    // Get all years from all occupations to determine the historical range
    val allYears = mutableSetOf<Int>()
    orderedStats().forEach { (_, occStats) ->
        allYears.addAll(occStats.years)
    }

    val minYear = minHistoricalYear ?: allYears.minOrNull() ?: 2020
    val maxYear = allYears.maxOrNull() ?: targetYear

    // Step 1: Backfill any missing years in the historical range
    val backfilledCollection = backfillMissingYears(minYear..maxYear)

    // Check if any years were backfilled
    val hasBackfilledData = backfilledCollection.regressionMetadata != null &&
            backfilledCollection.regressionMetadata?.projectedYears?.isNotEmpty() == true

    // Step 2: Project forward to target year if needed
    val finalCollection = if (maxYear < targetYear) {
        val yearsToProject = targetYear - maxYear
        backfilledCollection.addYears(yearsToProject)
    } else {
        backfilledCollection
    }

    return ProcessedStatsCollectionResult(
        statsCollection = finalCollection,
        hasBackfilledData = hasBackfilledData
    )
}

